import {
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { SecurityEvent } from "@xop/dashboard-shared";
import { isAnomalousAccess } from "@xop/dashboard-shared";
import type { JSX } from "react";

import { Pagination } from "@/components/ui/pagination";
import { detailSummary, formatTs, severityColor } from "./securityFormat.js";

/**
 * The Security console's event feed — a dense table (time / severity / source
 * / action / actor / outcome / IP / detail) with anomalous rows tinted +
 * flagged, plus the 25/page pager. Extracted from `SecurityPanel.tsx`; the
 * orchestrator owns the paging state and passes the current page in.
 */
export function SecurityFeedTable({
  events,
  page,
  perPage,
  totalCount,
  totalPages,
  onPageChange,
  onPerPageChange,
}: {
  events: SecurityEvent[];
  page: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  onPerPageChange: (n: number) => void;
}): JSX.Element {
  return (
    <Card>
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((e) => {
                const t = formatTs(e.ts);
                const anomaly = isAnomalousAccess(e);
                return (
                  <TableRow
                    key={e.id}
                    {...(anomaly
                      ? { sx: { bgcolor: (th: Theme) => alpha(th.palette.error.main, 0.08) } }
                      : {})}
                  >
                    <TableCell title={t.utc} sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                      {t.local}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={severityColor(e.severity)} label={e.severity} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: 14 }}>{e.source}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 12 }}>
                      {anomaly && <Box component="span" aria-hidden="true">⚠ </Box>}
                      {e.action}
                    </TableCell>
                    <TableCell sx={{ fontSize: 14 }}>{e.actor ?? "—"}</TableCell>
                    <TableCell sx={{ fontSize: 14 }}>
                      {anomaly ? (
                        <Chip size="small" color="error" label={e.outcome} />
                      ) : (
                        e.outcome
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{e.ip ?? "—"}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: "28ch",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontFamily: "monospace",
                        fontSize: 12,
                        color: "text.disabled",
                      }}
                      title={detailSummary(e.detail)}
                    >
                      {detailSummary(e.detail)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Pagination
            page={page}
            perPage={perPage}
            totalCount={totalCount}
            totalPages={totalPages}
            entityNoun="events"
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
